import React, { useState, useEffect, useCallback } from 'react';
import useJiraConnection, { JiraTestResult } from '../../hooks/useJiraConnection';
import './JiraConnectModal.css';

interface JiraConnectModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: () => void;
}

type Step = 'form' | 'testing' | 'success' | 'error';

const JiraConnectModal: React.FC<JiraConnectModalProps> = ({ open, onClose, onConnected }) => {
  const { settings, refetch, test, save, disconnect } = useJiraConnection();
  const [siteUrl, setSiteUrl] = useState('');
  const [email, setEmail] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [step, setStep] = useState<Step>('form');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<JiraTestResult | null>(null);

  // Pre-fill from current settings whenever the modal opens.
  useEffect(() => {
    if (open) {
      refetch();
      setStep('form');
      setMessage(null);
      setTestResult(null);
    }
  }, [open, refetch]);

  useEffect(() => {
    if (open && settings) {
      setSiteUrl(settings.siteUrl || '');
      setEmail(settings.email || '');
      // Never pre-fill the token — it's masked.
      setApiToken('');
    }
  }, [open, settings]);

  const canSubmit = siteUrl.trim() && email.trim() && apiToken.trim() && !busy;

  const handleTest = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    setStep('testing');
    setMessage(null);
    try {
      const result = await test({ siteUrl: siteUrl.trim(), email: email.trim(), apiToken: apiToken.trim() });
      setTestResult(result);
      setStep('success');
      setMessage(`Connected as ${result.displayName}${result.emailAddress ? ` (${result.emailAddress})` : ''}`);
    } catch (err: any) {
      setStep('error');
      setMessage(err.response?.data?.error || 'Connection test failed');
    } finally {
      setBusy(false);
    }
  }, [canSubmit, test, siteUrl, email, apiToken]);

  const handleSave = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await save({ siteUrl: siteUrl.trim(), email: email.trim(), apiToken: apiToken.trim() });
      setTestResult(result);
      setStep('success');
      setMessage(`Saved — connected as ${result.displayName}`);
      onConnected?.();
      setTimeout(() => onClose(), 900);
    } catch (err: any) {
      setStep('error');
      setMessage(err.response?.data?.error || 'Failed to save Jira settings');
    } finally {
      setBusy(false);
    }
  }, [canSubmit, save, siteUrl, email, apiToken, onConnected, onClose]);

  const handleDisconnect = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      await disconnect();
      setSiteUrl('');
      setEmail('');
      setApiToken('');
      setStep('form');
      setMessage('Jira connection removed.');
      onConnected?.();
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  }, [disconnect, onConnected]);

  if (!open) return null;

  return (
    <div className="jira-connect-overlay" onClick={onClose}>
      <div className="jira-connect-modal" onClick={(e) => e.stopPropagation()}>
        <div className="jira-connect-header">
          <h2>Connect Jira</h2>
          <button type="button" className="jira-connect-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="jira-connect-body">
          {settings?.configured && (
            <div className="jira-connect-status">
              <span className="jira-connect-dot connected" />
              <div>
                <div className="jira-connect-status-title">Jira is connected</div>
                <div className="jira-connect-status-sub">
                  {settings.siteUrl} · {settings.email}
                  {settings.configuredBy ? ` · set up by ${settings.configuredBy}` : ''}
                </div>
              </div>
            </div>
          )}

          <p className="jira-connect-help">
            Use an Atlassian API token with the account email. Create one at{' '}
            <a
              href="https://id.atlassian.com/manage-profile/security/api-tokens"
              target="_blank"
              rel="noreferrer"
            >
              Atlassian API tokens
            </a>
            . The token is encrypted at rest and shared org-wide.
          </p>

          <label className="jira-connect-label">
            <span>Site URL</span>
            <input
              type="url"
              value={siteUrl}
              onChange={(e) => { setSiteUrl(e.target.value); setStep('form'); }}
              placeholder="https://yourorg.atlassian.net"
              autoComplete="url"
              disabled={busy}
            />
          </label>

          <label className="jira-connect-label">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setStep('form'); }}
              placeholder="you@yourorg.com"
              autoComplete="email"
              disabled={busy}
            />
          </label>

          <label className="jira-connect-label">
            <span>API token</span>
            <input
              type="password"
              value={apiToken}
              onChange={(e) => { setApiToken(e.target.value); setStep('form'); }}
              placeholder={settings?.configured ? '•••••••• (enter a new token to replace)' : 'ATATT3xFfGF...'}
              autoComplete="off"
              disabled={busy}
            />
          </label>

          {step === 'success' && testResult && (
            <div className="jira-connect-result success">
              <div className="jira-connect-result-title">✓ {message}</div>
              <div className="jira-connect-result-sub">
                Account ID: {testResult.accountId}
              </div>
            </div>
          )}

          {step === 'error' && message && (
            <div className="jira-connect-result error">
              <div className="jira-connect-result-title">✕ {message}</div>
            </div>
          )}

          {step === 'testing' && (
            <div className="jira-connect-result testing">
              <div className="jira-connect-result-title">Testing connection…</div>
            </div>
          )}
        </div>

        <div className="jira-connect-footer">
          {settings?.configured && (
            <button
              type="button"
              className="jira-connect-btn danger"
              onClick={handleDisconnect}
              disabled={busy}
            >
              Disconnect
            </button>
          )}
          <div className="jira-connect-footer-spacer" />
          <button type="button" className="jira-connect-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="jira-connect-btn secondary"
            onClick={handleTest}
            disabled={!canSubmit}
          >
            Test
          </button>
          <button
            type="button"
            className="jira-connect-btn primary"
            onClick={handleSave}
            disabled={!canSubmit}
          >
            {settings?.configured ? 'Update & Save' : 'Save & Connect'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JiraConnectModal;
